import React, { useId } from "react";
import { motion, useReducedMotion, type Variants } from "framer-motion";
import { NudgeArrow } from "./NudgeArrow";
import { DISPLAY_HEADING } from "../utils/typography";
const ease = [0.23, 1, 0.32, 1] as const;
const leadWords = ["Progress,", "with"];
const highlightWord = "humanity";
const trailWord = "intact.";
const WORD_STAGGER = 0.06;
const WORD_START = 0.15;
// Paint begins once every word, including "intact.", has risen into place
const PAINT_DELAY = WORD_START + (leadWords.length + 2) * WORD_STAGGER + 0.25;
// Arrow lands as the paint finishes, matching the section headings
const ARROW_DELAY = PAINT_DELAY + 0.45;
const lineVariants: Variants = {
  hidden: {},
  shown: {
    transition: {
      staggerChildren: WORD_STAGGER,
      delayChildren: WORD_START
    }
  }
};
const wordVariants: Variants = {
  hidden: {
    y: "105%"
  },
  shown: {
    y: "0%",
    transition: {
      duration: 0.3,
      ease
    }
  }
};
const brushEase = [0.33, 0.9, 0.4, 1] as const;
export function Hero() {
  const reduce = useReducedMotion();
  const brushId = `brush-${useId().replace(/:/g, "")}`;
  return <section aria-labelledby="hero-heading" className="w-full" data-id="element-302" data-mp="3o7bzdvq44lfk">
      <div className="bg-cream px-6 pb-24 pt-36 md:px-12 md:pb-32 md:pt-52 lg:px-20 lg:pb-40" data-id="element-303" data-mp="3o5j4tesrxrnx">
        <div className="flex items-end gap-4 md:gap-[2vw]" data-id="element-304" data-mp="39a38120t7hnu">
          {/* Arrow — same placement and motion as the section headings */}
          <motion.span aria-hidden="true" className="block shrink-0 pb-[0.6vw]" initial={reduce ? false : {
          opacity: 0,
          x: -12,
          y: -12
        }} animate={{
          opacity: 1,
          x: 0,
          y: 0
        }} transition={{
          duration: 0.3,
          ease,
          delay: ARROW_DELAY
        }} data-id="element-305" data-mp="30iaxysfp6ajs">
            <NudgeArrow delay={ARROW_DELAY + 0.4} data-id="element-306" data-mp="2vieuqvetqn5a" />
          </motion.span>

          <motion.h1 id="hero-heading" aria-label="Progress, with humanity intact." className={`max-w-[16ch] ${DISPLAY_HEADING} text-espresso`} initial={reduce ? false : "hidden"} animate="shown" variants={lineVariants} data-id="element-307" data-mp="2vglpnkjv0aze">
            <span aria-hidden="true" data-id="element-308" data-mp="nbbeh30z0j7e">
              {leadWords.map(word => <span key={word} className="-my-[0.08em] inline-block overflow-hidden py-[0.08em] align-bottom" data-id="element-309" data-mp="n98dxp26z7of">
                  <motion.span variants={wordVariants} className="inline-block" data-id="element-310" data-mp="dd5cymj2tf32">
                    {word}
                  </motion.span>
                  <span className="inline-block" data-id="element-311" data-mp="1azuk1asmyhvs">&nbsp;</span>
                </span>)}

              {/* Extra side padding so the brush edges aren't clipped by the rise mask */}
              <span className="-mx-[0.18em] -my-[0.08em] inline-block overflow-hidden px-[0.18em] py-[0.08em] align-bottom" data-id="element-312" data-mp="111ybmqr3211d">
                <motion.span variants={wordVariants} className="relative isolate inline-block whitespace-nowrap" data-id="element-313" data-mp="x0l3ga0iplra">
                  {/* Painted highlight behind "humanity" */}
                  <svg className="absolute -left-[0.12em] top-[0.2em] -z-10 h-[0.82em] w-[calc(100%+0.24em)] overflow-visible" viewBox="0 0 400 100" preserveAspectRatio="none" data-id="element-314" data-mp="3hqahwlxb75jg">
                    <defs data-id="element-315" data-mp="269zihte7qxoe">
                      <filter id={brushId} x="-5%" y="-30%" width="110%" height="160%" data-id="element-316" data-mp="269hgsh8s7r93">
                        <feTurbulence type="fractalNoise" baseFrequency="0.04 0.25" numOctaves="2" seed="3" data-id="element-317" data-mp="2b0fz1mdbyzez" />
                        <feDisplacementMap in="SourceGraphic" scale="9" xChannelSelector="R" yChannelSelector="G" data-id="element-318" data-mp="2g0lrv5vc6ryn" />
                      </filter>
                    </defs>
                    <g filter={`url(#${brushId})`} data-id="element-319" data-mp="2p0h83p3r1d5j">
                      <motion.path d="M14 56 C 80 44, 160 62, 240 50 S 360 46, 388 52" fill="none" stroke="#e2a33a" strokeWidth="70" strokeLinecap="round" initial={reduce ? false : {
                      pathLength: 0
                    }} animate={{
                      pathLength: 1
                    }} transition={{
                      duration: 0.6,
                      ease: brushEase,
                      delay: PAINT_DELAY
                    }} data-id="element-320" data-mp="2p1sapllgkev2" />
                      <motion.path d="M30 26 C 120 20, 220 32, 300 24 S 370 26, 380 30" fill="none" stroke="#e2a33a" strokeWidth="12" strokeLinecap="round" opacity="0.7" initial={reduce ? false : {
                      pathLength: 0
                    }} animate={{
                      pathLength: 1
                    }} transition={{
                      duration: 0.5,
                      ease: brushEase,
                      delay: PAINT_DELAY + 0.08
                    }} data-id="element-321" data-mp="3vhfdc3wxjvhn" />
                      <motion.path d="M24 86 C 110 92, 210 80, 290 88 S 360 84, 376 80" fill="none" stroke="#e2a33a" strokeWidth="10" strokeLinecap="round" opacity="0.6" initial={reduce ? false : {
                      pathLength: 0
                    }} animate={{
                      pathLength: 1
                    }} transition={{
                      duration: 0.5,
                      ease: brushEase,
                      delay: PAINT_DELAY + 0.12
                    }} data-id="element-322" data-mp="3mou013c2ih07" />
                    </g>
                  </svg>
                  {highlightWord}
                </motion.span>
              </span>

              <span className="-my-[0.08em] inline-block overflow-hidden py-[0.08em] align-bottom" data-id="element-323" data-mp="1s9esd4998jej">
                <span className="inline-block" data-id="element-324" data-mp="1x8rnecaa4ws7">&nbsp;</span>
                <motion.span variants={wordVariants} className="inline-block" data-id="element-325" data-mp="1x65g8drawjai">
                  {trailWord}
                </motion.span>
              </span>
            </span>
          </motion.h1>
        </div>
      </div>
    </section>;
}